import { useEffect, useRef } from 'react';
import proj4 from 'proj4';
import { useLeafletContext } from '@react-leaflet/core';
import { useMap } from 'react-leaflet';
import parseGeoraster from 'georaster';
import GeoRasterLayer from 'georaster-layer-for-leaflet';
import { fromArrayBuffer } from 'geotiff';

window.proj4 = proj4;

// Improved helper function to extract min/max from XML metadata
const extractMinMax = (xmlString) => {
  if (!xmlString) return { min: null, max: null };

  console.log('Parsing GDAL metadata:', xmlString); // Try multiple patterns to extract min/max

  let min = null,
    max = null; // Pattern 1: <Item name="min" sample="0">0.0</Item>

    
  const minMatch1 = xmlString.match(/<Item[^>]*name="min"[^>]*>([-\d.eE]+)</);
  const maxMatch1 = xmlString.match(/<Item[^>]*name="max"[^>]*>([-\d.eE]+)</); // Pattern 2: Look for min/max in any XML format

  const minMatch2 = xmlString.match(/<min[^>]*>([-\d.eE]+)<\/min>/);
  const maxMatch2 = xmlString.match(/<max[^>]*>([-\d.eE]+)<\/max>/); // Pattern 3: Look for min/max as attributes

  const minMatch3 = xmlString.match(/min\s*=\s*["']([-\d.eE]+)["']/);
  const maxMatch3 = xmlString.match(/max\s*=\s*["']([-\d.eE]+)["']/);

  if (minMatch1) min = parseFloat(minMatch1[1]);
  else if (minMatch2) min = parseFloat(minMatch2[1]);
  else if (minMatch3) min = parseFloat(minMatch3[1]);

  if (maxMatch1) max = parseFloat(maxMatch1[1]);
  else if (maxMatch2) max = parseFloat(maxMatch2[1]);
  else if (maxMatch3) max = parseFloat(maxMatch3[1]);

  console.log('Extracted min/max:', { min, max });
  return { min, max };
};

// Generate color ramp from black to white (same as your logic)
const generateColorRamp = (min, max, numSteps = 7) => {
  if (min == null || max == null || max - min <= 0) return [];

  const steps = [];
  const range = max - min;

  for (let i = 0; i < numSteps; i++) {
    const value = min + (i / (numSteps - 1)) * range;
    const t = (value - min) / range;
    const gray = Math.round(t * 255);
    const color = `rgb(${gray},${gray},${gray})`;
    steps.push({ value: Number(value.toFixed(4)), color });
  }

  return steps;
};

// 🆕 Function to generate the HTML for the legend's color scale
const generateLegendHTML = (min, max, colors, noDataValue, unit = '') => {
  if (colors.length === 0) return '';

  const colorStops = colors.map((step) => step.color).join(', ');
  const backgroundStyle = `linear-gradient(to right, ${colorStops})`; // Create a single bar for the color gradient

  let html = `
    <div style="display: flex; align-items: center; margin-top: 5px; margin-bottom: 5px;">
      <div style="flex-grow: 1; height: 15px; background: ${backgroundStyle}; border: 1px solid #ccc;"></div>
    </div>
    <div style="display: flex; justify-content: space-between; font-size: 10px; font-weight: bold;">
      <span>${min.toFixed(2)} ${unit}</span>
      <span>${max.toFixed(2)} ${unit}</span>
    </div>
  `;
  return html;
};

const TiffLayer = ({ id, url, options, setLegend }) => {
  const geoTiffLayerRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const context = useLeafletContext();
  const map = useMap();

  useEffect(() => {
    // Skip if already loaded, no URL, or layer already exists
    if (hasLoadedRef.current || !url || geoTiffLayerRef.current) return;

    const loadTiff = async () => {
      try {
        console.log(`Loading TIFF layer: ${id}`);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch GeoTIFF: ${response.status} ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        let georaster; // 🛑 FIX 1: ArrayBuffer Detachment Issue // Create a copy for parseGeoraster since it might detach the buffer.

        const georasterBufferCopy = arrayBuffer.slice(0);

        try {
          georaster = await parseGeoraster(georasterBufferCopy);
          console.log('Georaster parsed successfully:', {
            mins: georaster.mins,
            maxs: georaster.maxs,
            noDataValue: georaster.noDataValue,
          });
        } catch (error) {
          console.error(`Failed to parse georaster for ${id}:`, error);
          return;
        } // Start with values from georaster

        let min = georaster.mins && georaster.mins.length > 0 ? georaster.mins[0] : null;
        let max = georaster.maxs && georaster.maxs.length > 0 ? georaster.maxs[0] : null;
        let noDataValue = georaster.noDataValue !== undefined ? georaster.noDataValue : null;
        let title = `Layer ${id}`; // Default title
        let unit = ''; // Default unit

        console.log('Initial values from georaster:', { min, max, noDataValue }); // Try to get metadata from TIFF - using the ORIGINAL arrayBuffer

        try {
          const tiff = await fromArrayBuffer(arrayBuffer);
          const image = await tiff.getImage();
          const fileDirectory = image.getFileDirectory();

          console.log('TIFF file directory:', fileDirectory); // Get GDAL metadata

          const gdalMetadata = fileDirectory.GDAL_METADATA;
          if (gdalMetadata) {
            console.log('Found GDAL metadata, parsing...');
            const { min: metaMin, max: metaMax } = extractMinMax(gdalMetadata);
            if (metaMin !== null) {
              min = metaMin;
              console.log('Using min from GDAL metadata:', min);
            }
            if (metaMax !== null) {
              max = metaMax;
              console.log('Using max from GDAL metadata:', max);
            }

            // 🆕 Extract Title and Unit from GDAL metadata
            const titleMatch = gdalMetadata.match(/<Item[^>]*name="title"[^>]*>([^<]+)</i);
            if (titleMatch) {
              title = titleMatch[1].trim();
            }
            const unitMatch = gdalMetadata.match(/<Item[^>]*name="UNITTYPE"[^>]*role="unittype"[^>]*>([^<]+)</i);
            if (unitMatch) {
              unit = unitMatch[1].trim();
            }
          } // Get noData value

          const tiffNoData = fileDirectory.GDAL_NODATA;
          if (tiffNoData !== undefined) {
            noDataValue = parseFloat(tiffNoData);
            console.log('Using noData value from TIFF:', noDataValue);
          }
        } catch (metadataError) {
          // This is where the 'detached ArrayBuffer' error was previously caught
          console.warn('Could not read TIFF metadata:', metadataError);
        } // Final fallbacks if we still don't have values

        if (min === null) {
          min = 0;
          console.log('Using fallback min value: 0');
        }
        if (max === null) {
          max = 1;
          console.log('Using fallback max value: 1');
        }
        if (max - min === 0) {
          max = min + 1;
          console.log('Adjusted max to avoid zero range:', max);
        }

        console.log(`Final TIFF ${id} value range: ${min} to ${max}, noData: ${noDataValue}`); // Create color function - black (min) to white (max)

        const range = max - min;
        const pixelValuesToColorFn = (values) => {
          const value = values[0]; // Handle noData values (transparent)

          if (value === noDataValue || value === null || isNaN(value)) {
            return null;
          } // Normalize to 0-1 range and convert to grayscale

          const normalized = (value - min) / range;
          const clamped = Math.max(0, Math.min(1, normalized));
          const gray = Math.round(clamped * 255);
          return `rgb(${gray}, ${gray}, ${gray})`;
        }; // Create GeoRaster layer

        const layerOptions = {
          ...options,
          georaster,
          pixelValuesToColorFn,
          resolution: options.resolution || 256,
        };

        const geoRasterLayer = new GeoRasterLayer(layerOptions);
        geoRasterLayer.id = id;
        geoTiffLayerRef.current = geoRasterLayer; // Add to map

        const container = context.layerContainer || map;
        container.addLayer(geoRasterLayer); // Fit bounds if available

        if (geoRasterLayer.getBounds) {
          try {
            map.fitBounds(geoRasterLayer.getBounds());
          } catch (boundsError) {
            console.warn(`Could not fit bounds for ${id}:`, boundsError);
          }
        } // Create comprehensive legend data

        const colors = generateColorRamp(min, max, 7); // 🛑 FIX 2: Generate the HTML for the legend content
        const legendTextHTML = generateLegendHTML(min, max, colors, noDataValue, unit);

        const legendData = {
          id,
          title: title, // Use extracted title
          min: min,
          max: max,
          noDataValue: noDataValue,
          colors: colors,
          colorScale: 'black-to-white',
          range: range,
          unit: unit, // Use extracted unit
          text: legendTextHTML, // ⬅️ IMPORTANT: Add the generated HTML here
        };

        setLegend('add', legendData);
        hasLoadedRef.current = true;
        console.log(`TIFF layer ${id} loaded successfully with legend data:`, legendData);
      } catch (error) {
        console.error(`Error loading TIFF layer ${id}:`, error);
      }
    };

    loadTiff();
  }, [id, url, map, context, setLegend, options]); // Cleanup effect

  useEffect(() => {
    return () => {
      if (geoTiffLayerRef.current) {
        console.log(`Cleaning up TIFF layer: ${id}`);
        const container = context.layerContainer || map;
        try {
          if (container.hasLayer(geoTiffLayerRef.current)) {
            container.removeLayer(geoTiffLayerRef.current);
          }
        } catch (error) {
          console.warn(`Error removing layer ${id}:`, error);
        }
        setLegend('remove', { id });
        geoTiffLayerRef.current = null;
        hasLoadedRef.current = false;
      }
    };
  }, [id, map, context, setLegend]);

  return null;
};

export default TiffLayer;
